// Microphone level: one scale for every mic surface (LUM-82).
//
// The scale is the /mic-test meter's: RMS deviation of the analyser's
// byte time-domain data from its 128 midpoint, doubled, capped at 255
// (~14 is a whisper, 30+ normal speech). The in-call capture path sees
// PCM16 instead of analyser bytes; levelFromPcm16 maps it onto the
// same scale so pre-call, in-call, and /mic-test numbers compare.
//
// Privacy: only numbers leave this module — never samples.

/** A level at or above this means the mic carries a voice. Above the
 *  noise-suppressed silence floor, below a whisper. */
export const MIC_HEARD_THRESHOLD = 6;

/** In-call: this long without a heard level while the interviewer
 *  awaits the respondent = "we can't hear you". */
export const MIC_FLAT_MS = 15_000;

/** In-call level breadcrumb cadence. */
export const AUDIO_LEVEL_SAMPLE_INTERVAL_MS = 15_000;

/** Coarse log-ish buckets: telemetry needs "dead / quiet / speaking",
 *  not a waveform. A level reports as the largest bucket ≤ it. */
const LEVEL_BUCKETS = [0, 2, 4, 8, 16, 32, 64, 128] as const;

export function levelFromTimeDomain(buf: Uint8Array): number {
  if (buf.length === 0) return 0;
  let sumSq = 0;
  for (let i = 0; i < buf.length; i++) {
    const d = buf[i] - 128;
    sumSq += d * d;
  }
  return Math.min(255, Math.round(Math.sqrt(sumSq / buf.length) * 2));
}

/** PCM16 sample s sits at byte 128 + s/256 on the analyser scale, so
 *  the doubled byte RMS is the sample RMS / 128. */
export function levelFromPcm16(samples: Int16Array): number {
  if (samples.length === 0) return 0;
  let sumSq = 0;
  for (let i = 0; i < samples.length; i++) {
    sumSq += samples[i] * samples[i];
  }
  return Math.min(255, Math.round(Math.sqrt(sumSq / samples.length) / 128));
}

export function bucketLevel(level: number): number {
  let bucket: number = LEVEL_BUCKETS[0];
  for (const b of LEVEL_BUCKETS) {
    if (level >= b) bucket = b;
  }
  return bucket;
}

export interface MicLevelSample {
  /** Bucketed mean level over the window. */
  levelBucket: number;
  /** Bucketed peak level over the window. */
  peakBucket: number;
  /** Level frames received in the window; 0 = capture delivered
   *  nothing (dead path), as opposed to a silent mic. */
  frameCount: number;
}

/** Aggregates per-chunk levels into one bucketed sample per window. */
export class MicLevelSampler {
  private sum = 0;
  private peak = 0;
  private frames = 0;
  private timer: number | null = null;

  constructor(private readonly onSample: (sample: MicLevelSample) => void) {}

  start(): void {
    this.stop();
    this.timer = window.setInterval(
      () => this.flush(),
      AUDIO_LEVEL_SAMPLE_INTERVAL_MS,
    );
  }

  push(level: number): void {
    this.sum += level;
    this.frames += 1;
    if (level > this.peak) this.peak = level;
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.sum = 0;
    this.peak = 0;
    this.frames = 0;
  }

  private flush(): void {
    const frameCount = this.frames;
    const mean = frameCount > 0 ? Math.round(this.sum / frameCount) : 0;
    const peak = this.peak;
    this.sum = 0;
    this.peak = 0;
    this.frames = 0;
    this.onSample({
      levelBucket: bucketLevel(mean),
      peakBucket: bucketLevel(peak),
      frameCount,
    });
  }
}

/** "Flat while awaiting the respondent": the window opens when the
 *  interviewer starts waiting and restarts on every heard level. */
export class MicSilenceDetector {
  private quietSince: number | null = null;

  setAwaiting(awaiting: boolean, now: number): void {
    this.quietSince = awaiting ? now : null;
  }

  onLevel(level: number, now: number): void {
    if (this.quietSince !== null && level >= MIC_HEARD_THRESHOLD) {
      this.quietSince = now;
    }
  }

  isFlat(now: number): boolean {
    return this.quietSince !== null && now - this.quietSince >= MIC_FLAT_MS;
  }
}
